import { useEffect, useState, useRef, useCallback } from "react";
import Cropper from "react-easy-crop";
import { api, formatApiErrorDetail } from "../lib/api";
import { Upload, Trash2, Plus, X, RefreshCw, Image as ImageIcon, ChevronDown, ChevronUp, Save, Award, Newspaper, Briefcase, Users, Quote, Crop, FileText, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [
  { key: "hero", label: "Hero (action shot)", aspect: "aspect-[16/10]", ratio: 16 / 10 },
  { key: "portrait", label: "Portrait", aspect: "aspect-[3/4]", ratio: 3 / 4 },
  { key: "gallery_1", label: "Gallery 1", aspect: "aspect-square", ratio: 1 },
  { key: "gallery_2", label: "Gallery 2", aspect: "aspect-square", ratio: 1 },
  { key: "gallery_3", label: "Gallery 3", aspect: "aspect-square", ratio: 1 },
  { key: "gallery_4", label: "Gallery 4", aspect: "aspect-square", ratio: 1 },
];

export default function EditorialEditor({ userId }) {
  const [open, setOpen] = useState(false);
  const [editorial, setEditorial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [photoBusy, setPhotoBusy] = useState({});
  const [photoTick, setPhotoTick] = useState(0); // cache-bust photo URLs after upload
  const [cropper, setCropper] = useState(null);   // { slot, ratio, src }
  const [autofillBusy, setAutofillBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/media-kit/editorial");
      setEditorial({
        career_highlights: data.career_highlights || [],
        honours: data.honours || [],
        media_coverage: data.media_coverage || [],
        past_partnerships: data.past_partnerships || [],
        pull_quote: data.pull_quote || "",
        audience_demographics: data.audience_demographics || {},
        photos_present: data.photos_present || {},
        uploaded_kit: data.uploaded_kit || null,
      });
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      setMsg({ type: "", text: "" });
      const payload = {
        career_highlights: editorial.career_highlights.filter(c => c.club),
        honours: editorial.honours.filter(h => h && h.trim()),
        media_coverage: editorial.media_coverage.filter(m => m.publication),
        past_partnerships: editorial.past_partnerships.filter(p => p.brand_name),
        pull_quote: editorial.pull_quote || "",
        audience_demographics: editorial.audience_demographics || {},
      };
      await api.patch("/media-kit/editorial", payload);
      setMsg({ type: "ok", text: "Editorial sections saved." });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  // AI auto-fill: call the preview-mode endpoint and merge suggestions into the
  // form. We DON'T overwrite filled rows — only blank slots get the suggestion,
  // so an athlete who's part-way through doesn't lose work. They click Save to
  // commit. Daily rate-limit is enforced server-side.
  const autofillFromProfile = async () => {
    setAutofillBusy(true); setMsg({ type: "", text: "" });
    try {
      const { data } = await api.post("/ai/media-kit/regenerate-editorial?preview=true");
      setEditorial(prev => {
        const cur = prev || {};
        const isBlankCareer = !cur.career_highlights || cur.career_highlights.every(r => !r.club);
        const isBlankHonours = !cur.honours || cur.honours.every(h => !h || !h.trim());
        const isBlankPartners = !cur.past_partnerships || cur.past_partnerships.every(p => !p.brand_name);
        // The backend now returns rich {club, period, appearances, goals, role}
        // objects, but we re-shape defensively so a partial response can't drop
        // form rows below the type the editor renders.
        const aiHighlights = (data.career_highlights || []).map(h => ({
          club: h.club || "",
          period: h.period || "",
          appearances: h.appearances ?? null,
          goals: h.goals ?? null,
          role: h.role || "",
        })).filter(r => r.club);
        const aiHonours = (data.honours || []).map(h => (typeof h === "string" ? h : (h?.value || ""))).filter(Boolean);
        const aiPartners = (data.past_partnerships || []).map(p => ({
          brand_name: p.brand_name || p.brand || "",
          year: p.year || "",
        })).filter(p => p.brand_name);
        return {
          ...cur,
          career_highlights: isBlankCareer && aiHighlights.length ? aiHighlights : cur.career_highlights,
          honours: isBlankHonours && aiHonours.length ? aiHonours : cur.honours,
          past_partnerships: isBlankPartners && aiPartners.length ? aiPartners : cur.past_partnerships,
          pull_quote: cur.pull_quote && cur.pull_quote.trim() ? cur.pull_quote : (data.pull_quote || ""),
          audience_demographics: (cur.audience_demographics && Object.keys(cur.audience_demographics).length)
            ? cur.audience_demographics
            : (data.audience_demographics || {}),
        };
      });
      setMsg({ type: "ok", text: "Filled from your profile. Review the rows, edit anything, then hit Save." });
    } catch (e) {
      setMsg({ type: "err",
               text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setAutofillBusy(false);
    }
  };

  const openCropper = (slotKey, file) => {
    if (!file) return;
    const slotDef = SLOTS.find(s => s.key === slotKey);
    const reader = new FileReader();
    reader.onload = () => setCropper({ slot: slotKey, ratio: slotDef.ratio, src: reader.result });
    reader.readAsDataURL(file);
  };

  const uploadCropped = async (slot, blob) => {
    setPhotoBusy(s => ({ ...s, [slot]: true })); setMsg({ type: "", text: "" });
    try {
      const fd = new FormData(); fd.append("file", blob, `${slot}.jpg`);
      await api.post(`/media-kit/photo?slot=${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); setPhotoTick(t => t + 1);
      setMsg({ type: "ok", text: `${slot.replace("_", " ")} uploaded.` });
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setPhotoBusy(s => ({ ...s, [slot]: false })); setCropper(null); }
  };

  const deletePhoto = async (slot) => {
    setPhotoBusy(s => ({ ...s, [slot]: true }));
    try { await api.delete(`/media-kit/photo?slot=${slot}`); await load(); setPhotoTick(t => t + 1); }
    catch (e) { setMsg({ type: "err", text: e.message }); }
    finally { setPhotoBusy(s => ({ ...s, [slot]: false })); }
  };

  // ---- Uploaded media-kit file (PDF / image from Canva, etc.) ----
  const [kitUploading, setKitUploading] = useState(false);
  const kitInputRef = useRef(null);
  const uploadKitFile = async (file) => {
    if (!file) return;
    const maxBytes = 12 * 1024 * 1024;
    if (file.size > maxBytes) { setMsg({ type: "err", text: "File too large — max 12MB" }); return; }
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMsg({ type: "err", text: "Only PDF, JPEG, PNG or WebP files are accepted" }); return;
    }
    setKitUploading(true); setMsg({ type: "", text: "" });
    try {
      const fd = new FormData(); fd.append("file", file, file.name || "media-kit");
      await api.post("/media-kit/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ type: "ok", text: "Media kit uploaded — brands will receive this as an attachment." });
      await load();
    } catch (e) { setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
    finally { setKitUploading(false); }
  };
  const deleteKitFile = async () => {
    if (!window.confirm("Remove your uploaded media kit? Outreach emails will fall back to the auto-generated one-pager.")) return;
    try { await api.delete("/media-kit/upload-file"); await load(); setMsg({ type: "ok", text: "Uploaded kit removed." }); }
    catch (e) { setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
  };

  if (!editorial) return null;

  const photoUrl = (slot) => `${BACKEND}/api/media-kit/photo/${userId}/${slot}?t=${photoTick}`;

  return (
    <div className="surface mt-6" data-testid="editorial-editor">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 hover:bg-bg-elev transition-colors" data-testid="editorial-toggle">
        <div className="flex items-center gap-3">
          <ImageIcon className="w-5 h-5 text-volt" />
          <div className="text-left">
            <div className="font-heading text-lg uppercase tracking-tight">Editorial sections</div>
            <div className="text-xs text-ink-muted">Photos, career highlights, honours, media coverage, partnerships</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-ink-muted" /> : <ChevronDown className="w-5 h-5 text-ink-muted" />}
      </button>

      {open && (
        <div className="p-6 border-t border-line space-y-8">
          {msg.text && (
            <div className={`px-4 py-3 text-sm ${msg.type === "ok" ? "border border-volt/50 text-volt" : "border border-[#FF3B30]/50 text-[#FF3B30]"}`} data-testid="editorial-msg">
              {msg.text}
            </div>
          )}

          {/* PHOTOS */}
          <section data-testid="editorial-photos">
            <div className="label-eyebrow mb-3 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Photos</div>
            <p className="text-xs text-ink-muted mb-4">Upload a hero action shot, a portrait, and up to 4 gallery photos. Up to 8MB each, auto-compressed.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SLOTS.map(s => (
                <PhotoSlot
                  key={s.key}
                  slot={s}
                  hasPhoto={!!editorial.photos_present[s.key]}
                  url={editorial.photos_present[s.key] ? photoUrl(s.key) : null}
                  busy={!!photoBusy[s.key]}
                  onPickFile={(f) => openCropper(s.key, f)}
                  onDelete={() => deletePhoto(s.key)}
                />
              ))}
            </div>
          </section>

          {/* UPLOADED MEDIA KIT FILE (Canva PDF, designed image…) */}
          <section className="mt-8" data-testid="uploaded-kit-section">
            <div className="label-eyebrow mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Uploaded media kit</div>
            <p className="text-xs text-ink-muted mb-4">
              Already designed your kit in Canva, Figma, or InDesign? Upload it here. We&apos;ll attach this PDF (or image) to your outreach emails instead of auto-generating one.
            </p>
            <input ref={kitInputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => uploadKitFile(e.target.files?.[0])} className="hidden" data-testid="uploaded-kit-file-input" />
            {editorial.uploaded_kit ? (
              <div className="surface border-l-2 border-volt p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="uploaded-kit-row">
                <div className="min-w-0 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-volt shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="uploaded-kit-name">{editorial.uploaded_kit.filename}</div>
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">
                      {(editorial.uploaded_kit.size / 1024 / 1024).toFixed(2)} MB · {editorial.uploaded_kit.mime?.split("/")[1]?.toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a href={`${BACKEND}/api/media-kit/file/${userId}?t=${photoTick}`} target="_blank" rel="noreferrer" data-testid="uploaded-kit-preview-btn"
                    className="text-xs uppercase tracking-widest border border-line px-3 py-2 hover:bg-bg-elev inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> Preview
                  </a>
                  <button onClick={() => kitInputRef.current?.click()} disabled={kitUploading} data-testid="uploaded-kit-replace-btn"
                    className="text-xs uppercase tracking-widest border border-line px-3 py-2 hover:bg-bg-elev">
                    {kitUploading ? "Uploading…" : "Replace"}
                  </button>
                  <button onClick={deleteKitFile} data-testid="uploaded-kit-delete-btn"
                    className="text-xs uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-3 py-2 hover:bg-[#FF3B30]/10">
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => kitInputRef.current?.click()} disabled={kitUploading} data-testid="uploaded-kit-upload-btn"
                className="w-full border-2 border-dashed border-line hover:border-volt hover:bg-bg-elev/40 p-6 transition-colors text-center disabled:opacity-50">
                <Upload className="w-5 h-5 mx-auto mb-2 text-ink-muted" />
                <div className="text-sm">{kitUploading ? "Uploading…" : "Upload media kit (PDF, JPG, PNG or WebP)"}</div>
                <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">Max 12MB · attached to all your outreach emails</div>
              </button>
            )}
          </section>

          {/* AUTO-FILL — single biggest activation lever in the kit creation flow.
              Pulls career highlights / honours / pull quote / audience demos from
              the athlete's profile + social_connections via Claude, then DROPS the
              suggestions into the form so the athlete reviews + edits before save.
              Athletes most often abandon here because writing achievements feels
              like a CV exercise; pre-filling cuts that work by ~70%. */}
          <section className="border border-volt/40 bg-volt/5 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5" data-testid="editorial-autofill-card">
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow text-volt mb-1.5">✨ AI auto-fill</div>
              <div className="text-sm text-white font-medium">Pre-fill this from your profile</div>
              <div className="text-xs text-ink-muted mt-1 leading-relaxed">
                Generates career highlights, honours, a pull quote and audience demographics from your profile data. Review + edit before you save — nothing is overwritten without your confirmation.
              </div>
            </div>
            <button
              onClick={autofillFromProfile}
              disabled={autofillBusy}
              data-testid="editorial-autofill-btn"
              className="btn-volt inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${autofillBusy ? "animate-spin" : ""}`} />
              {autofillBusy ? "Generating…" : "Auto-fill from profile"}
            </button>
          </section>

          {/* CAREER HIGHLIGHTS */}
          <ListEditor
            testid="editorial-career"
            icon={<Award className="w-3.5 h-3.5" />}
            label="Career highlights"
            items={editorial.career_highlights}
            onChange={(items) => setEditorial({ ...editorial, career_highlights: items })}
            blank={{ club: "", period: "", appearances: null, goals: null, role: "" }}
            renderRow={(item, idx, update) => (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <input placeholder="Club / Team" value={item.club} onChange={e => update("club", e.target.value)} className={cellCls} data-testid={`career-${idx}-club`} />
                <input placeholder="Period (e.g. 2022—Present)" value={item.period} onChange={e => update("period", e.target.value)} className={cellCls} data-testid={`career-${idx}-period`} />
                <input placeholder="Role (optional)" value={item.role} onChange={e => update("role", e.target.value)} className={cellCls} data-testid={`career-${idx}-role`} />
                <input type="number" placeholder="Appearances" value={item.appearances ?? ""} onChange={e => update("appearances", e.target.value === "" ? null : parseInt(e.target.value, 10))} className={cellCls} data-testid={`career-${idx}-apps`} />
                <input type="number" placeholder="Goals" value={item.goals ?? ""} onChange={e => update("goals", e.target.value === "" ? null : parseInt(e.target.value, 10))} className={cellCls} data-testid={`career-${idx}-goals`} />
              </div>
            )}
          />

          {/* HONOURS */}
          <ListEditor
            testid="editorial-honours"
            icon={<Award className="w-3.5 h-3.5" />}
            label="Honours & titles"
            items={editorial.honours.map(h => ({ value: h }))}
            onChange={(items) => setEditorial({ ...editorial, honours: items.map(i => i.value || "") })}
            blank={{ value: "" }}
            renderRow={(item, idx, update) => (
              <input placeholder="e.g. 2024 League Winner" value={item.value} onChange={e => update("value", e.target.value)} className={cellCls} data-testid={`honour-${idx}`} />
            )}
          />

          {/* MEDIA COVERAGE */}
          <ListEditor
            testid="editorial-media"
            icon={<Newspaper className="w-3.5 h-3.5" />}
            label="Media coverage"
            items={editorial.media_coverage}
            onChange={(items) => setEditorial({ ...editorial, media_coverage: items })}
            blank={{ publication: "", headline: "", url: "" }}
            renderRow={(item, idx, update) => (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input placeholder="Publication" value={item.publication} onChange={e => update("publication", e.target.value)} className={cellCls} data-testid={`media-${idx}-pub`} />
                <input placeholder="Headline" value={item.headline} onChange={e => update("headline", e.target.value)} className={cellCls} data-testid={`media-${idx}-headline`} />
                <input placeholder="URL (optional)" value={item.url} onChange={e => update("url", e.target.value)} className={cellCls} data-testid={`media-${idx}-url`} />
              </div>
            )}
          />

          {/* PAST PARTNERSHIPS */}
          <ListEditor
            testid="editorial-partners"
            icon={<Briefcase className="w-3.5 h-3.5" />}
            label="Past brand partnerships"
            items={editorial.past_partnerships}
            onChange={(items) => setEditorial({ ...editorial, past_partnerships: items })}
            blank={{ brand_name: "", year: "" }}
            renderRow={(item, idx, update) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input placeholder="Brand name" value={item.brand_name} onChange={e => update("brand_name", e.target.value)} className={cellCls} data-testid={`partner-${idx}-name`} />
                <input placeholder="Year(s)" value={item.year} onChange={e => update("year", e.target.value)} className={cellCls} data-testid={`partner-${idx}-year`} />
              </div>
            )}
          />

          {/* PULL QUOTE */}
          <section data-testid="editorial-quote">
            <div className="label-eyebrow mb-3 flex items-center gap-2"><Quote className="w-3.5 h-3.5" /> Pull quote</div>
            <textarea
              placeholder='e.g. "I run faster on Sundays."'
              value={editorial.pull_quote}
              onChange={(e) => setEditorial({ ...editorial, pull_quote: e.target.value })}
              maxLength={500} rows={3}
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white italic"
              data-testid="pull-quote-input"
            />
          </section>

          {/* AUDIENCE DEMOGRAPHICS */}
          <DemographicsEditor
            demo={editorial.audience_demographics}
            onChange={(d) => setEditorial({ ...editorial, audience_demographics: d })}
          />

          {/* SAVE */}
          <div className="flex justify-end pt-4 border-t border-line">
            <button onClick={save} disabled={saving} className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="editorial-save">
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save editorial sections"}
            </button>
          </div>
        </div>
      )}

      {cropper && (
        <CropModal
          slot={cropper.slot}
          ratio={cropper.ratio}
          src={cropper.src}
          onCancel={() => setCropper(null)}
          onConfirm={(blob) => uploadCropped(cropper.slot, blob)}
        />
      )}
    </div>
  );
}

const cellCls = "w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-sm text-white";

function PhotoSlot({ slot, hasPhoto, url, busy, onPickFile, onDelete }) {
  const fileRef = useRef(null);
  return (
    <div className={`relative border border-line ${slot.aspect} bg-bg-elev overflow-hidden group`} data-testid={`photo-slot-${slot.key}`}>
      {hasPhoto && url ? (
        <>
          <img src={url} alt={slot.label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-volt text-black p-2 hover:bg-white" title="Replace" data-testid={`photo-replace-${slot.key}`}>
              <Upload className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} disabled={busy} className="bg-black/80 text-[#FF3B30] p-2 hover:bg-black" title="Delete" data-testid={`photo-delete-${slot.key}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      ) : (
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full h-full flex flex-col items-center justify-center gap-2 hover:bg-bg-surface transition-colors" data-testid={`photo-upload-${slot.key}`}>
          {busy ? <RefreshCw className="w-5 h-5 text-volt animate-spin" /> : <Upload className="w-5 h-5 text-ink-muted" />}
          <div className="text-[10px] uppercase tracking-widest text-ink-muted text-center px-2">{slot.label}</div>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { onPickFile(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

function ListEditor({ testid, icon, label, items, onChange, blank, renderRow }) {
  const add = () => onChange([...items, { ...blank }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, key, val) => onChange(items.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow flex items-center gap-2">{icon}{label}</div>
        <button onClick={add} className="text-xs uppercase tracking-widest text-volt hover:text-white inline-flex items-center gap-1" data-testid={`${testid}-add`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-ink-dim italic">No entries yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="flex-1">{renderRow(item, idx, (k, v) => update(idx, k, v))}</div>
              <button onClick={() => remove(idx)} className="p-2 text-ink-muted hover:text-[#FF3B30]" data-testid={`${testid}-${idx}-delete`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DemographicsEditor({ demo, onChange }) {
  const upd = (k, v) => onChange({ ...demo, [k]: v === "" ? null : Number(v) });
  const updCountry = (idx, key, val) => {
    const list = [...(demo.top_countries || [])];
    list[idx] = { ...list[idx], [key]: key === "pct" ? (val === "" ? null : Number(val)) : val };
    onChange({ ...demo, top_countries: list });
  };
  const addCountry = () => onChange({ ...demo, top_countries: [...(demo.top_countries || []), { country: "", pct: null }] });
  const removeCountry = (idx) => onChange({ ...demo, top_countries: (demo.top_countries || []).filter((_, i) => i !== idx) });
  return (
    <section data-testid="editorial-demographics">
      <div className="label-eyebrow mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Audience demographics</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <NumField label="Followers" value={demo.follower_count} onChange={(v) => upd("follower_count", v)} testid="demo-followers" />
        <NumField label="Avg impressions" value={demo.avg_impressions} onChange={(v) => upd("avg_impressions", v)} testid="demo-impressions" />
        <NumField label="Avg reach" value={demo.avg_reach} onChange={(v) => upd("avg_reach", v)} testid="demo-reach" />
        <NumField label="Engagement rate %" value={demo.engagement_rate_pct} onChange={(v) => upd("engagement_rate_pct", v)} testid="demo-engagement" step="0.1" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
        <NumField label="Female %" value={demo.female_pct} onChange={(v) => upd("female_pct", v)} testid="demo-female" max={100} />
        <NumField label="Age 18-24 %" value={demo.age_18_24_pct} onChange={(v) => upd("age_18_24_pct", v)} testid="demo-age1" max={100} />
        <NumField label="Age 25-34 %" value={demo.age_25_34_pct} onChange={(v) => upd("age_25_34_pct", v)} testid="demo-age2" max={100} />
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow text-[10px]">Top countries</div>
        <button onClick={addCountry} className="text-xs uppercase tracking-widest text-volt hover:text-white inline-flex items-center gap-1" data-testid="demo-country-add">
          <Plus className="w-3 h-3" /> Add country
        </button>
      </div>
      <div className="space-y-2">
        {(demo.top_countries || []).map((c, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input placeholder="Country (e.g. GBR, KSA, USA)" value={c.country || ""} onChange={(e) => updCountry(idx, "country", e.target.value)} className={cellCls + " flex-1"} data-testid={`demo-country-${idx}-name`} />
            <input type="number" placeholder="%" max={100} value={c.pct ?? ""} onChange={(e) => updCountry(idx, "pct", e.target.value)} className={cellCls + " w-24"} data-testid={`demo-country-${idx}-pct`} />
            <button onClick={() => removeCountry(idx)} className="p-2 text-ink-muted hover:text-[#FF3B30]" data-testid={`demo-country-${idx}-delete`}><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function NumField({ label, value, onChange, testid, step = "1", max }) {
  return (
    <div>
      <label className="label-eyebrow text-[10px] block mb-1">{label}</label>
      <input type="number" step={step} max={max} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cellCls} data-testid={testid} />
    </div>
  );
}


function CropModal({ slot, ratio, src, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => { setCroppedAreaPixels(areaPixels); }, []);

  const confirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, croppedAreaPixels);
      onConfirm(blob);
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crop-modal">
      <div className="bg-bg border border-line max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Crop className="w-3 h-3 text-volt" /> Crop {slot.replace("_", " ")}</div>
            <div className="text-xs text-ink-muted mt-1">Drag to position, scroll to zoom. Image will be saved at the optimal aspect ratio.</div>
          </div>
          <button onClick={onCancel} className="text-ink-muted hover:text-white" data-testid="crop-cancel"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative flex-1 bg-bg-elev" style={{ minHeight: 400 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={ratio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            restrictPosition={false}
            objectFit="contain"
          />
        </div>
        <div className="px-5 py-4 border-t border-line space-y-3">
          <div className="flex items-center gap-3">
            <span className="label-eyebrow text-[10px]">Zoom</span>
            <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-volt" data-testid="crop-zoom" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onCancel} disabled={busy} className="btn-ghost-line">Cancel</button>
            <button onClick={confirm} disabled={busy || !croppedAreaPixels} className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="crop-confirm">
              {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {busy ? "Uploading…" : "Save crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: render a cropped JPEG blob from a data-URL source + crop region (pixels).
async function getCroppedBlob(src, area) {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error("Crop failed")), "image/jpeg", 0.92);
  });
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
